import React, { useState, useEffect } from 'react';
import { Modal, Form, DatePicker, Select, Input, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs, { Dayjs } from 'dayjs';
import { createLeaveRequest, updateLeaveRequest, getAttendanceRecord, createAttendanceRecord, updateAttendanceRecord } from '@/api/employees';
import { useAuth } from '@/contexts/AuthContext';
import { EmployeeSelector } from '@/components/common/EmployeeSelector';
import type { LeaveRequest } from '@/types/employee/attendance';

const { TextArea } = Input;


interface LeaveRequestModalProps {
  open: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  editingRequest?: LeaveRequest | null;
}

interface FormValues {
  employeeId: string;
  startDate: Dayjs;
  endDate: Dayjs;
  type: 'Annual' | 'Sick' | 'Unpaid' | 'Other';
  reason: string;
  status?: 'Pending' | 'Approved' | 'Rejected';
  approvedById?: string;
}

export const LeaveRequestModal: React.FC<LeaveRequestModalProps> = ({
  open,
  onCancel,
  onSuccess,
  editingRequest
}) => {
  const { t } = useTranslation(['attendance', 'common', 'employees']);
  const { firestoreUser } = useAuth();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<FormValues>();
  
  // State for employee data
  const [employeeData, setEmployeeData] = useState<{ id: string; name: string } | null>(null);
  const [approverData, setApproverData] = useState<{ id: string; name: string } | null>(null);

  // Mutations
  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return createLeaveRequest(data, firestoreUser);
    },
    onSuccess: () => {
      message.success(t('attendance:messages.leaveRequestCreated'));
      queryClient.invalidateQueries({ queryKey: ['leaveRequests'] });
      onSuccess();
    },
    onError: (error: Error) => {
      message.error(error.message || t('attendance:messages.leaveRequestError'));
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return updateLeaveRequest(id, data, firestoreUser);
    },
    onSuccess: () => {
      message.success(t('attendance:messages.leaveRequestUpdated'));
      queryClient.invalidateQueries({ queryKey: ['leaveRequests'] });
      onSuccess();
    },
    onError: (error: Error) => {
      message.error(error.message || t('attendance:messages.leaveRequestError'));
    }
  });

  // Initialize form when editing
  useEffect(() => {
    if (editingRequest && open) {
      // Handle Firestore timestamps
      const startDate = editingRequest.startDate?.toDate ? editingRequest.startDate.toDate() : editingRequest.startDate;
      const endDate = editingRequest.endDate?.toDate ? editingRequest.endDate.toDate() : editingRequest.endDate;
      
      form.setFieldsValue({
        employeeId: editingRequest.employee.id,
        startDate: dayjs(startDate),
        endDate: dayjs(endDate),
        type: editingRequest.type,
        reason: editingRequest.reason,
        status: editingRequest.status,
        approvedById: editingRequest.approvedBy?.id
      });
      setEmployeeData({
        id: editingRequest.employee.id,
        name: editingRequest.employee.name
      });
      if (editingRequest.approvedBy) {
        setApproverData({
          id: editingRequest.approvedBy.id,
          name: editingRequest.approvedBy.name
        });
      }
    } else if (!editingRequest && open) {
      form.resetFields();
      setEmployeeData(null);
      setApproverData(null);
    }
  }, [editingRequest, open, form]);

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  // Function to create attendance records for approved leave
  const createAttendanceForLeave = async (employee: { id: string; name: string }, startDate: Date, endDate: Date) => {
    try {
      const start = dayjs(startDate);
      const end = dayjs(endDate);
      const days = end.diff(start, 'day') + 1;

      // Create attendance records for each day
      for (let i = 0; i < days; i++) {
        const currentDate = start.add(i, 'day');
        const day = currentDate.date();
        const month = currentDate.month() + 1; // dayjs months are 0-indexed
        const year = currentDate.year();

        // Check if attendance record already exists for this date
        const existingAttendance = await getAttendanceRecord(day, month, year, firestoreUser!);

        if (existingAttendance) {
          // Update existing attendance record
          const existingEmployees = existingAttendance.employee || [];
          const employeeIndex = existingEmployees.findIndex((emp: any) => emp.id === employee.id);

          if (employeeIndex >= 0) {
            // Update existing employee record
            existingEmployees[employeeIndex] = {
              ...existingEmployees[employeeIndex],
              status: 'On Leave'
            };
          } else {
            // Add new employee record
            existingEmployees.push({
              id: employee.id,
              name: employee.name,
              status: 'On Leave'
            });
          }

          await updateAttendanceRecord(existingAttendance.id, {
            employee: existingEmployees
          }, firestoreUser!);
        } else {
          // Create new attendance record with only the employee on leave
          await createAttendanceRecord({
            employee: [{
              id: employee.id,
              name: employee.name,
              status: 'On Leave'
            }],
            day,
            month,
            year,
            notes: `Auto-generated for approved leave request`
          }, firestoreUser!);
        }
      }

      message.success(t('attendance:messages.attendanceCreatedForLeave'));
    } catch (error) {
      console.error('Error creating attendance for leave:', error);
      message.error(t('attendance:messages.attendanceCreationError'));
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      if (!employeeData) {
        message.error(t('attendance:errors.selectEmployee'));
        return;
      }

      const data = {
        employee: {
          id: employeeData.id,
          name: employeeData.name
        },
        startDate: values.startDate.toDate(),
        endDate: values.endDate.toDate(),
        type: values.type,
        reason: values.reason,
        status: values.status || 'Pending'
      };

      if (editingRequest) {
        // Update existing request
        const updateData: any = { ...data };
        
        if (values.approvedById && approverData) {
          updateData.approvedBy = {
            id: approverData.id,
            name: approverData.name
          };
          updateData.approvedAt = new Date();
        }

        await updateMutation.mutateAsync({ id: editingRequest.id, data: updateData });

        // If status changed to Approved, create attendance records
        if (values.status === 'Approved' && editingRequest.status !== 'Approved') {
          await createAttendanceForLeave(employeeData!, values.startDate.toDate(), values.endDate.toDate());
        }
      } else {
        // Create new request
        await createMutation.mutateAsync(data);

        // If status is Approved, create attendance records
        if (values.status === 'Approved') {
          await createAttendanceForLeave(employeeData!, values.startDate.toDate(), values.endDate.toDate());
        }
      }
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setEmployeeData(null);
    setApproverData(null);
    onCancel();
  };

  return (
    <Modal
      title={editingRequest ? t('attendance:editLeaveRequest') : t('attendance:newLeaveRequest')}
      open={open}
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={isSubmitting}
      width={600}
      okText={editingRequest ? t('common:actions.update') : t('common:actions.create')}
      cancelText={t('common:actions.cancel')}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          type: 'Annual',
          status: 'Pending'
        }}
      >
        <Form.Item
          name="employeeId"
          label={t('employees:fields.name')}
          rules={[{ required: true, message: t('common:fieldRequired') }]}
        >
          <EmployeeSelector
            onEmployeeDataChange={setEmployeeData}
            disabled={!!editingRequest} // Disable employee selection when editing
          />
        </Form.Item>

        <Form.Item
          name="startDate"
          label={t('attendance:startDate')}
          rules={[{ required: true, message: t('common:fieldRequired') }]}
        >
          <DatePicker
            style={{ width: '100%' }}
            format="DD/MM/YYYY"
            placeholder={t('attendance:selectStartDate')}
          />
        </Form.Item>

        <Form.Item
          name="endDate"
          label={t('attendance:endDate')}
          rules={[{ required: true, message: t('common:fieldRequired') }]}
        >
          <DatePicker
            style={{ width: '100%' }}
            format="DD/MM/YYYY"
            placeholder={t('attendance:selectEndDate')}
          />
        </Form.Item>

        <Form.Item
          name="type"
          label={t('attendance:leaveType')}
          rules={[{ required: true, message: t('common:fieldRequired') }]}
        >
          <Select placeholder={t('attendance:selectLeaveType')}>
            <Select.Option value="Annual">{t('attendance:leaveTypes.annual')}</Select.Option>
            <Select.Option value="Sick">{t('attendance:leaveTypes.sick')}</Select.Option>
            <Select.Option value="Unpaid">{t('attendance:leaveTypes.unpaid')}</Select.Option>
            <Select.Option value="Other">{t('attendance:leaveTypes.other')}</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="reason"
          label={t('attendance:reason')}
          rules={[{ required: true, message: t('common:fieldRequired') }]}
        >
          <TextArea
            rows={4}
            placeholder={t('attendance:reasonPlaceholder')}
          />
        </Form.Item>

        <Form.Item
          name="status"
          label={t('common:statusLabel')}
          rules={[{ required: true, message: t('common:fieldRequired') }]}
        >
          <Select placeholder={t('attendance:selectStatus')}>
            <Select.Option value="Pending">{t('attendance:status.pending')}</Select.Option>
            <Select.Option value="Approved">{t('attendance:status.approved')}</Select.Option>
            <Select.Option value="Rejected">{t('attendance:status.rejected')}</Select.Option>
          </Select>
        </Form.Item>

        {editingRequest && (
          <Form.Item
            name="approvedById"
            label={t('attendance:approvedBy')}
          >
            <EmployeeSelector
              onEmployeeDataChange={setApproverData}
              disabled={false}
            />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}; 